import React, { useRef, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import scrollBar from '@components/userDocuments/editDocument/UI/Editor/shared/scrollBar';
import DropdownListBox from './DropdownListBox';
import breakpointsMinWidth from '../../../styling/breakpointsMinWidth';

export interface ListItem {
  item: string | React.JSX.Element;
  startIcon?: React.JSX.Element;
  shortcut?: string;
  triggerFunction: () => void;
  hasPopover?: boolean;
  popoverData?: ItemsList;
  noCloseonSelect?: boolean;
  loading?: boolean;
  loader?: React.JSX.Element;
  isDisabled?: boolean;
  isHighlighted?: boolean;
  dataTestId?: string;
}

export type ItemsList = ListItem[] | ListItem[][];

interface CustomizedDropdownProps {
  itemsList: ItemsList;
  sectioned: boolean;
  isOpen: boolean;
  closeDropdown: () => void;
  styleClasses?: string | undefined;
  itemStyleClasses?: string | undefined;
  top?: string | undefined;
  left?: string | undefined;
  border?: boolean | undefined;
  showTooltip?: boolean | undefined;
  onScroll?: (e: React.UIEvent<HTMLDivElement>) => void;
}
interface DropdownWrapperProps {
  top: string;
  left: string;
  isOpen: boolean;
}

const anim = keyframes`
    0%{
        opacity: 0;
        scale: 0.8;
    }
    100%{
        opacity: 1;
        scale: 1;
    }
`;

const DropdownWrapper = styled.div<DropdownWrapperProps>`
  display: ${({ isOpen }) => (isOpen ? 'block' : 'none')};
  top: ${({ top }) => `${top + 18}px`};
  left: ${({ left }) => `${left + 48}px`};
  border-radius: 14px;
  box-shadow: 0px 0px 8px 2px rgba(0, 0, 0, 0.15);
  animation: ${anim} 0.15s ease;
  ${() => scrollBar({ width: '6px', height: '6px', trackColor: 'transparent' })}

  @media ${breakpointsMinWidth.tabletSm} {
    top: ${({ top }) => `${top}px`};
    left: ${({ left }) => `${left}px`};
  }
`;

const defaultProps = {
  styleClasses: '',
  itemStyleClasses: '',
  top: '10',
  left: '20',
  border: true,
  showTooltip: false,
  onScroll: () => {},
};

const CustomizedDropdown: React.FC<CustomizedDropdownProps> = ({
  itemsList,
  sectioned,
  styleClasses,
  itemStyleClasses,
  isOpen,
  closeDropdown,
  top = '-110',
  left = '20',
  border = true,
  showTooltip,
  onScroll,
}) => {
  const dropdownRef = useRef<HTMLDivElement>(null);

  const handleClickOutside = (e) => {
    if (
      dropdownRef &&
      dropdownRef.current &&
      !dropdownRef.current.contains(e.target)
    ) {
      closeDropdown();
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const positioningClasses = `absolute bg-white z-10`;
  const defaultStylingClasses = `bg-white w-[220px] tabletSm:w-[268px]`;
  const customStylingClasses = styleClasses || defaultStylingClasses;

  return (
    <DropdownWrapper
      ref={dropdownRef}
      className={`${positioningClasses} ${customStylingClasses}`}
      top={top}
      left={left}
      isOpen={isOpen}
      onScroll={onScroll}
    >
      <DropdownListBox
        itemsList={itemsList}
        closeDropdown={closeDropdown}
        sectioned={sectioned}
        itemStyleClasses={itemStyleClasses}
        border={border}
        showTooltip={showTooltip}
      />
    </DropdownWrapper>
  );
};

CustomizedDropdown.defaultProps = defaultProps;

export default CustomizedDropdown;
